import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ChevronDown, ChevronRight, Plus, Trash2, 
  Cpu, MemoryStick, HardDrive, Monitor, Battery, Wifi, Usb, Settings, Info, Plug, Box, Ruler,
  Camera, MonitorSmartphone, Laptop, Smartphone, CircuitBoard
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Icon mapping
const ICON_MAP = {
  Cpu, MemoryStick, HardDrive, Monitor, Battery, Wifi, Usb, Settings, Info, Plug, Box, Ruler,
  Camera, MonitorSmartphone, Laptop, Smartphone, CircuitBoard
};

/**
 * DynamicSpecsForm - Renders a dynamic form based on category schema
 * 
 * Props:
 * - schema: CategorySchema object with field_groups
 * - specs: Current specs object (nested JSON)
 * - onChange: Callback when specs change
 * - errors: Array of validation errors
 * - readOnly: Boolean for view mode
 */
export default function DynamicSpecsForm({ schema, specs = {}, onChange, errors = [], readOnly = false }) {
  const [openGroups, setOpenGroups] = useState(new Set(
    schema?.field_groups?.slice(0, 3).map(g => g.key) || []
  ));

  useEffect(() => {
    setOpenGroups(new Set(schema?.field_groups?.slice(0, 3).map(g => g.key) || []));
  }, [schema?.id, schema?.field_groups]);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const updateSpec = (groupKey, fieldKey, value, arrayIndex = null) => {
    const newSpecs = { ...specs };
    
    if (arrayIndex !== null) {
      // Array group (like armazenamento)
      if (!Array.isArray(newSpecs[groupKey])) {
        newSpecs[groupKey] = [];
      }
      if (!newSpecs[groupKey][arrayIndex]) {
        newSpecs[groupKey][arrayIndex] = {};
      }
      newSpecs[groupKey][arrayIndex][fieldKey] = value;
    } else {
      // Regular group
      if (!newSpecs[groupKey]) {
        newSpecs[groupKey] = {};
      }
      newSpecs[groupKey][fieldKey] = value;
    }
    
    onChange(newSpecs);
  };

  const addArrayItem = (groupKey) => {
    const newSpecs = { ...specs };
    if (!Array.isArray(newSpecs[groupKey])) {
      newSpecs[groupKey] = [];
    }
    newSpecs[groupKey].push({});
    onChange(newSpecs);
  };

  const removeArrayItem = (groupKey, index) => {
    const newSpecs = { ...specs };
    if (Array.isArray(newSpecs[groupKey])) {
      newSpecs[groupKey].splice(index, 1);
      onChange(newSpecs);
    }
  };

  const getFieldValue = (groupKey, fieldKey, arrayIndex = null) => {
    if (arrayIndex !== null) {
      return specs[groupKey]?.[arrayIndex]?.[fieldKey] ?? '';
    }
    return specs[groupKey]?.[fieldKey] ?? '';
  };

  const hasErrorsInGroup = (groupKey) => {
    const groupLabel = schema?.field_groups?.find((group) => group.key === groupKey)?.label || groupKey;
    return errors.some((errorMessage) => errorMessage.includes(groupLabel));
  };

  const renderField = (field, groupKey, arrayIndex = null) => {
    const value = getFieldValue(groupKey, field.key, arrayIndex);
    const fieldId = arrayIndex !== null 
      ? `${groupKey}-${arrayIndex}-${field.key}`
      : `${groupKey}-${field.key}`;

    if (readOnly) {
      return (
        <div key={fieldId} className="space-y-1">
          <p className="text-xs text-muted-foreground">{field.label}</p>
          <p className="text-sm font-medium">
            {value || '-'}
            {field.unit && value && ` ${field.unit}`}
          </p>
        </div>
      );
    }

    return (
      <div key={fieldId} className="space-y-1">
        <Label htmlFor={fieldId} className="text-xs flex items-center gap-1">
          {field.label}
          {field.required && <span className="text-red-500">*</span>}
          {field.unit && <span className="text-muted-foreground">({field.unit})</span>}
        </Label>
        
        {field.type === 'select' && field.options ? (
          <Select
            value={value?.toString() || ''}
            onValueChange={(v) => updateSpec(groupKey, field.key, v, arrayIndex)}
          >
            <SelectTrigger className="h-9" data-testid={`spec-field-${fieldId}`}>
              <SelectValue placeholder={field.placeholder || `Seleccionar ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : field.type === 'boolean' ? (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={fieldId}
              checked={!!value}
              onChange={(e) => updateSpec(groupKey, field.key, e.target.checked, arrayIndex)}
              className="h-4 w-4 rounded border-gray-300"
              data-testid={`spec-field-${fieldId}`}
            />
            <span className="text-sm text-muted-foreground">Sim</span>
          </div>
        ) : (
          <Input
            id={fieldId}
            type={field.type === 'number' || field.type === 'integer' ? 'number' : 'text'}
            step={field.type === 'number' ? '0.1' : '1'}
            min={field.min_value}
            max={field.max_value}
            value={value}
            onChange={(e) => {
              let val = e.target.value;
              if (field.type === 'integer') val = val ? parseInt(val) : '';
              if (field.type === 'number') val = val ? parseFloat(val) : '';
              updateSpec(groupKey, field.key, val, arrayIndex);
            }}
            placeholder={field.placeholder}
            className="h-9"
            data-testid={`spec-field-${fieldId}`}
          />
        )}
      </div>
    );
  };

  const renderArrayGroup = (group) => {
    const items = Array.isArray(specs[group.key]) ? specs[group.key] : [];
    
    return (
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={item?.id || item?.key || `${group.key}-${typeof item === 'object' ? JSON.stringify(item) : String(item)}-${index}`} className="relative border rounded-lg p-3 bg-white">
            <div className="absolute top-2 right-2">
              {!readOnly && items.length > 1 && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                  onClick={() => removeArrayItem(group.key, index)}
                  data-testid={`remove-spec-array-item-${group.key}-${index}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3 pr-8">
              {group.fields.map(field => renderField(field, group.key, index))}
            </div>
          </div>
        ))}
        
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="w-full"
            onClick={() => addArrayItem(group.key)}
            data-testid={`add-spec-array-item-${group.key}`}
          >
            <Plus className="h-3 w-3 mr-1" />
            Adicionar {group.label}
          </Button>
        )}
      </div>
    );
  };

  if (!schema || !schema.field_groups) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        <p>Seleccione uma categoria para ver os campos técnicos</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-sm font-medium text-red-800">Campos obrigatórios em falta:</p>
          <ul className="list-disc list-inside text-xs text-red-700 mt-1">
            {errors.map((err) => <li key={err}>{err}</li>)}
          </ul>
        </div>
      )}
      
      {schema.field_groups.map(group => {
        const IconComponent = ICON_MAP[group.icon] || Settings;
        const isOpen = openGroups.has(group.key);
        const hasErrors = hasErrorsInGroup(group.key);
        
        // Count required fields in group
        const requiredCount = group.fields.filter(f => f.required).length;
        
        return (
          <div key={group.key}>
            <Button
              type="button"
              variant="ghost"
              className={cn(
                "w-full justify-between h-10 px-3 hover:bg-slate-100",
                hasErrors && "border-red-300 bg-red-50",
                isOpen && "bg-slate-100"
              )}
              onClick={() => toggleGroup(group.key)}
              data-testid={`spec-group-toggle-${group.key}`}
            >
              <div className="flex items-center gap-2">
                <IconComponent className="h-4 w-4 text-indigo-600" />
                <span className="font-medium">{group.label}</span>
                {requiredCount > 0 && (
                  <Badge variant="outline" className="text-xs h-5">
                    {requiredCount} obrigatórios
                  </Badge>
                )}
              </div>
              {isOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
            {isOpen && (
              <div className="pt-2 pb-3 px-3 border-l-2 border-indigo-100 ml-2" data-testid={`spec-group-content-${group.key}`}>
                {group.is_array ? (
                  renderArrayGroup(group)
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {group.fields.map(field => renderField(field, group.key))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
